import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeliveryRequestsWithDetails } from '../services/adminDeliveryService';
import DeliveryTrackingMap from '../components/DeliveryTrackingMap';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { getStatusLabel } from '../utils/statusLabels';
import { REQUESTS_TABLE_ID } from '../lib/appwrite';
import './RequestsPage.css';

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const getRequestStatus = (r) => {
  const status = (r.status || '').toLowerCase();
  if (status) return status;
  if (r.isComplete) return 'completed';
  if (r.isTaken) return 'in_progress';
  return 'pending';
};


const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
];

const RequestsPage = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [trackingRequestId, setTrackingRequestId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await getDeliveryRequestsWithDetails();
      setRequests(rows || []);
    } catch (err) {
      const msg = err.message || 'Failed to load delivery requests';
      const isAuthError =
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized'));
      setError(
        isAuthError
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env for server-level access.`
          : msg
      );
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [REQUESTS_TABLE_ID] });

  const filteredRequests = requests.filter((r) => {
    const status = getRequestStatus(r);
    const matchStatus = !filterStatus || status === filterStatus;
    const q = search.toLowerCase().trim();
    const matchSearch =
      !q ||
      (r.store?.name || r.storeName || '').toLowerCase().includes(q) ||
      (r.store?.contact || r.storeContact || '').toLowerCase().includes(q) ||
      (r.customer?.name || '').toLowerCase().includes(q) ||
      (r.customer?.email || '').toLowerCase().includes(q) ||
      (r.customer?.phone || '').toLowerCase().includes(q) ||
      (r.packageName || '').toLowerCase().includes(q) ||
      (r.orderId || '').toLowerCase().includes(q) ||
      (r.driver?.name || r.driverName || '').toLowerCase().includes(q) ||
      (r.driver?.contact || r.driverContact || '').toLowerCase().includes(q) ||
      (r.trackingNumber || '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const canTrack = (r) =>
    r.dropLatitude &&
    r.dropLongitude &&
    ((r.pickLatitude && r.pickLongitude) || r.storeId);

  if (loading && requests.length === 0) {
    return (
      <div className="requests-loading">
        <div className="dashboard-spinner" />
        <p>Loading delivery requests...</p>
      </div>
    );
  }

  return (
    <div className="requests-page">
      <div className="requests-header">
        <div>
          <h1 className="dashboard-title">Delivery Requests</h1>
          <p className="dashboard-subtitle">
            Monitor delivery requests and track packages in real time.
          </p>
        </div>
      </div>

      {error && (
        <div className="requests-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="requests-toolbar">
        <div className="requests-search-wrap">
          <img src="/icons/Search.svg" alt="" className="requests-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search by store, customer, package, order, driver..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="requests-search"
          />
        </div>
        <div className="requests-status-tabs">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id || 'all'}
              type="button"
              className={`requests-status-tab ${filterStatus === f.id ? 'active' : ''}`}
              onClick={() => setFilterStatus(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" className="requests-btn-refresh" onClick={loadData}>
          ⟳ Refresh
        </button>
      </div>

      <div className="requests-table-wrap">
        <table className="requests-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Customer</th>
              <th>Package</th>
              <th>Order</th>
              <th>Created</th>
              <th>Status</th>
              <th>Driver</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan={9} className="requests-empty">
                  No delivery requests found.
                </td>
              </tr>
            ) : (
              filteredRequests.map((r) => {
                const status = getRequestStatus(r);
                return (
                  <tr key={r.$id}>
                    <td>
                      <div className="requests-entity-cell">
                        <span className="requests-entity-name">{r.store?.name || r.storeName || '—'}</span>
                        {r.store?.contact && (
                          <span className="requests-entity-meta">{r.store.contact}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="requests-entity-cell">
                        <span className="requests-entity-name">{r.customer?.name || '—'}</span>
                        {(r.customer?.phone || r.customer?.email) && (
                          <span className="requests-entity-meta">
                            {r.customer.phone || r.customer.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{r.packageName || '—'}</td>
                    <td className="requests-mono">{r.orderId ? String(r.orderId).slice(0, 12) + (r.orderId.length > 12 ? '…' : '') : '—'}</td>
                    <td>{formatDate(r.createdAt || r.$createdAt)}</td>
                    <td>
                      <span className={`requests-status requests-status-${status.replace(' ', '-')}`}>
                        {getStatusLabel(t, status)}
                      </span>
                    </td>
                    <td>
                      <div className="requests-entity-cell">
                        <span className="requests-entity-name">{r.driver?.name || r.driverName || r.driverId || '—'}</span>
                        {r.driver?.contact && (
                          <span className="requests-entity-meta">{r.driver.contact}</span>
                        )}
                      </div>
                    </td>
                    <td>{formatCurrency(r.price)}</td>
                    <td>
                      {canTrack(r) ? (
                        <button
                          type="button"
                          className="requests-track-btn"
                          onClick={() => setTrackingRequestId(r.$id)}
                        >
                          Track
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {trackingRequestId && (
        <DeliveryTrackingMap
          requestId={trackingRequestId}
          onClose={() => setTrackingRequestId(null)}
        />
      )}
    </div>
  );
};

export default RequestsPage;
